import React from 'react';
import { Liga } from '../types/liga';
import { Post } from '../services/postService';
import PostRouter from './PostRouter';
import Load from './Load';
import Createpost from './Createpost';
import { CurrentProfile } from '../types/profile';
import LeagueSidebar from './LeagueSidebar';
import LeagueTournamentsTab from './LeagueTournamentsTab';
import LeagueAthletesList from './LeagueAthletesList';
import { Organizador } from '../services/organizadorService';
import { Torneio } from '../services/torneioService';
import { Atleta } from '../services/ligaService';
import { StatusTorneio } from '../utils/StatusTorneio';

export type LeagueTabId = 'principal' | 'torneios' | 'jogadores' | 'seguidores';

interface LeagueTabsProps {
  liga: Liga;
  organizadores: Organizador[];
  organizadoresLoading: boolean;
  organizadorPrincipal: Organizador | null;
  isAdmin: boolean;
  isOwnLeague: boolean;
  seguidorUserId: string;
  followId: string | null;
  loadingSeguir: boolean;
  erroSeguir: string;
  onSeguir: () => Promise<void>;
  formatarDataCriacao: (dataString: string) => string;
  formatarTelefoneWhatsApp: (telefone: string | null | undefined) => string;
  posts: Post[];
  postsLoading: boolean;
  postsError: string | null;
  page: number;
  hasMore: boolean;
  onLoadMore: () => void;
  onPostCreated?: () => void;
  getPostviewProps: (post: Post) => { torneioId?: string; readOnly: boolean; ligaId?: string };
  /** Perfil ativo derivado pela page, repassado aos componentes de post. */
  perfilAtivo?: CurrentProfile | null;
  activeTab: LeagueTabId;
  onTabChange: (tab: LeagueTabId) => void;
  onVerTorneios: () => void;
  torneios: Torneio[];
  torneiosLoading: boolean;
  torneiosError: string | null;
  torneiosStatusFilter: StatusTorneio | null;
  onTorneiosStatusFilterChange: (status: StatusTorneio | null) => void;
  jogadores: Atleta[];
  jogadoresLoading: boolean;
  jogadoresError: string | null;
  seguidores: Atleta[];
  seguidoresLoading: boolean;
  seguidoresError: string | null;
  isAtleta: boolean;
  isLiga: boolean;
  atletaId: string;
  userId: string | undefined;
}

const LeagueTabs: React.FC<LeagueTabsProps> = ({
  liga,
  organizadores,
  organizadoresLoading,
  organizadorPrincipal,
  isAdmin,
  isOwnLeague,
  seguidorUserId,
  followId,
  loadingSeguir,
  erroSeguir,
  onSeguir,
  formatarDataCriacao,
  formatarTelefoneWhatsApp,
  posts,
  postsLoading,
  postsError,
  page,
  hasMore,
  onLoadMore,
  onPostCreated,
  getPostviewProps,
  perfilAtivo,
  activeTab,
  onTabChange,
  onVerTorneios,
  torneios,
  torneiosLoading,
  torneiosError,
  torneiosStatusFilter,
  onTorneiosStatusFilterChange,
  jogadores,
  jogadoresLoading,
  jogadoresError,
  seguidores,
  seguidoresLoading,
  seguidoresError,
  isAtleta,
  isLiga,
  atletaId,
  userId,
}) => {
  return (
    <div className="card w-100 shadow-xss rounded-xxl border-0 mb-3">
      <div className="card-body p-0">
        {/* Abas */}
        <ul className="nav nav-tabs border-0 px-3 pt-3" role="tablist">
          <li className="nav-item" role="presentation">
            <button
              className={`nav-link ${activeTab === 'principal' ? 'active' : ''}`}
              id="principal-tab"
              type="button"
              role="tab"
              aria-controls="principal"
              aria-selected={activeTab === 'principal'}
              onClick={() => onTabChange('principal')}
            >
              Principal
            </button>
          </li>
          <li className="nav-item" role="presentation">
            <button
              className={`nav-link ${activeTab === 'torneios' ? 'active' : ''}`}
              id="torneios-tab"
              type="button"
              role="tab"
              aria-controls="torneios"
              aria-selected={activeTab === 'torneios'}
              onClick={() => onTabChange('torneios')}
            >
              Torneios
            </button>
          </li>
          <li className="nav-item" role="presentation">
            <button
              className={`nav-link ${activeTab === 'jogadores' ? 'active' : ''}`}
              id="jogadores-tab"
              type="button"
              role="tab"
              aria-controls="jogadores"
              aria-selected={activeTab === 'jogadores'}
              onClick={() => onTabChange('jogadores')}
            >
              Jogadores
            </button>
          </li>
          <li className="nav-item" role="presentation">
            <button
              className={`nav-link ${activeTab === 'seguidores' ? 'active' : ''}`}
              id="seguidores-tab"
              type="button"
              role="tab"
              aria-controls="seguidores"
              aria-selected={activeTab === 'seguidores'}
              onClick={() => onTabChange('seguidores')}
            >
              Seguidores
            </button>
          </li>
        </ul>

        {/* Divisória */}
        <div className="border-top"></div>

        {/* Conteúdo das Abas */}
        <div className="tab-content p-3">
          <div
            className={`tab-pane fade ${activeTab === 'principal' ? 'show active' : ''}`}
            id="principal"
            role="tabpanel"
            aria-labelledby="principal-tab"
          >
            <div className="row">
              {/* Coluna Esquerda */}
              <LeagueSidebar
                liga={liga}
                organizadores={organizadores}
                organizadoresLoading={organizadoresLoading}
                organizadorPrincipal={organizadorPrincipal}
                isAdmin={isAdmin}
                isOwnLeague={isOwnLeague}
                seguidorUserId={seguidorUserId}
                followId={followId}
                loadingSeguir={loadingSeguir}
                erroSeguir={erroSeguir}
                onSeguir={onSeguir}
                formatarDataCriacao={formatarDataCriacao}
                formatarTelefoneWhatsApp={formatarTelefoneWhatsApp}
                onVerTorneios={onVerTorneios}
              />

              {/* Coluna Direita - 2/3 */}
              <div className="col-md-8">
                <div className="border-start ps-md-4">
                  {isOwnLeague && (
                    <Createpost
                      ligaId={liga.id}
                      perfilAtivo={perfilAtivo}
                      onPostCreated={onPostCreated}
                    />
                  )}
                  {postsLoading && page === 0 && <Load />}
                  {postsError && (
                    <div className="alert alert-danger" role="alert">
                      {postsError}
                    </div>
                  )}
                  {!postsLoading && posts.length === 0 && (
                    <div className="card w-100 shadow-xss rounded-xxl border-0 mb-3">
                      <div className="card-body text-center">
                        <p className="mb-0">Nenhum post encontrado.</p>
                      </div>
                    </div>
                  )}
                  {posts.map((post) => {
                    const props = getPostviewProps(post);
                    return (
                      <PostRouter
                        key={post.id}
                        post={post}
                        torneioId={props.torneioId}
                        ligaId={props.ligaId}
                        perfilAtivo={perfilAtivo}
                        readOnly={props.readOnly}
                      />
                    );
                  })}
                  {hasMore && posts.length > 0 && (
                    <div className="text-center">
                      <button className="btn btn-primary" onClick={onLoadMore}>
                        Carregar mais
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
          <div
            className={`tab-pane fade ${activeTab === 'torneios' ? 'show active' : ''}`}
            id="torneios"
            role="tabpanel"
            aria-labelledby="torneios-tab"
          >
            <LeagueTournamentsTab
              ligaId={liga.id}
              torneios={torneios}
              loading={torneiosLoading}
              error={torneiosError}
              statusFilter={torneiosStatusFilter}
              onStatusFilterChange={onTorneiosStatusFilterChange}
              isAtleta={isAtleta}
              isLiga={isLiga}
              atletaId={atletaId}
              userId={userId}
            />
          </div>
          <div
            className={`tab-pane fade ${activeTab === 'jogadores' ? 'show active' : ''}`}
            id="jogadores"
            role="tabpanel"
            aria-labelledby="jogadores-tab"
          >
            <LeagueAthletesList
              atletas={jogadores}
              loading={jogadoresLoading}
              error={jogadoresError}
              titulo="jogadores"
            />
          </div>
          <div
            className={`tab-pane fade ${activeTab === 'seguidores' ? 'show active' : ''}`}
            id="seguidores"
            role="tabpanel"
            aria-labelledby="seguidores-tab"
          >
            <LeagueAthletesList
              atletas={seguidores}
              loading={seguidoresLoading}
              error={seguidoresError}
              titulo="seguidores"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default LeagueTabs;
